import { useCallback, useMemo, useRef } from "react";
import type {
  EditorContents,
  SetSelectedId,
  SetTreePill,
  TreePill,
} from "./editorContents";
import { stagedContentFor } from "./editorContents";
import type { StagedLists } from "./pipelineStaging";
import type { SchemaDrafts, ScriptDrafts } from "./stagingDrafts";
import {
  deselectTarget,
  editorToReset,
  firstSchemalessStageId,
  isPendingAnywhere,
  isPendingOn,
  isSchemaEditorId,
  isUntitledScriptId,
  SCHEMA_EDITOR_ID,
  SINK_EDITOR_ID,
  SOURCE_EDITOR_ID,
  type StagedId,
  type StagedKind,
  type StagedResource,
  type StagedRole,
  type StagedSchema,
  type StageTabId,
  type StageTabRole,
  schemaEditorId,
  schemaIdOfInstance,
  schemaInstanceId,
  selectionAfterUnstage,
  TRANSFORM_EDITOR_ID,
  toggleAttachment,
  unattachedSchemaEditorId,
  unstageHoldsSelection,
  withoutPendingOn,
  withSchemaRenamed,
  withStagedSchema,
  withStagedSchemaAttached,
} from "./stagingModel";

// Selection and staging behaviors on top of the raw staged lists, editor
// contents and drafts: what a row click, drop, rename or removal does to
// the single nav-bar selection and the editor on screen.

// Staging a schema (drop or editor save) also selects its row and loads its
// stored definition into the schema editor, so the dropped/created schema
// is immediately the one on screen (Figma 14442-316243/-316244). Staging
// into a slot consumes that slot's unsaved draft (the staged schema takes
// the slot over).
export function useSchemaStaging(options: {
  lists: StagedLists;
  loadSchema: (resource: StagedResource, onLoaded?: () => void) => void;
  setEditorSource: SetTreePill;
  setSelectedId: SetSelectedId;
  clearDraftFor: (rowId: string) => void;
}) {
  const { lists, loadSchema, setEditorSource, setSelectedId, clearDraftFor } =
    options;
  // A dropped/saved schema selects the row it just landed as — the
  // per-block INSTANCE in its destination block, so exactly that row
  // lights up (not the schema's other rows). The selection moves only
  // once the definition is committed to the editor: switching first would
  // flash the schema editor's placeholder for the frames the fetch is in
  // flight (the drop-transition blink).
  const selectStagedSchema = useCallback(
    (resource: StagedResource, blockKey: string) => {
      loadSchema(resource, () => {
        setSelectedId(schemaInstanceId(resource.id, blockKey));
        setEditorSource(null);
      });
    },
    [loadSchema, setEditorSource, setSelectedId],
  );
  const stageSchema = useCallback(
    (resource: StagedResource, home: StageTabRole = "sink") => {
      lists.setSchemas((prev) => withStagedSchema(prev, resource, home));
      clearDraftFor(unattachedSchemaEditorId(home));
      selectStagedSchema(resource, unattachedSchemaEditorId(home));
    },
    [lists.setSchemas, clearDraftFor, selectStagedSchema],
  );
  // Attaches a schema to a stage's slot, consuming the slot's draft, and
  // leaves the selection alone. The drop-time reuse of a stored schema
  // (variantSwitch) lands while the dropped stage's own row is being
  // selected — pulling the selection onto the schema would swap the
  // editor from the sample document the user just asked for.
  const attachSchema = useCallback(
    (resource: StagedResource, target: { role: StagedRole; id: StagedId }) => {
      lists.setSchemas((prev) =>
        withStagedSchemaAttached(prev, resource, target),
      );
      clearDraftFor(schemaEditorId(target.id));
    },
    [lists.setSchemas, clearDraftFor],
  );
  const stageSchemaFor = useCallback(
    (resource: StagedResource, target: { role: StagedRole; id: StagedId }) => {
      attachSchema(resource, target);
      selectStagedSchema(resource, schemaEditorId(target.id));
    },
    [attachSchema, selectStagedSchema],
  );
  // A schema drafted in a bottom block belongs to "the next stage" of that
  // tab: when one already arrived mid-draft and still lacks a schema,
  // saving attaches to it; otherwise the schema waits unattached (homed on
  // its tab) for a future stage to adopt it (schemasAfterStageStaged).
  const stageDraftedSchema = useCallback(
    (resource: StagedResource, role: StageTabRole = "sink") => {
      clearDraftFor(unattachedSchemaEditorId(role));
      const items = {
        source: lists.sources,
        sink: lists.sinks,
        transform: lists.transforms,
      }[role];
      const stageId = firstSchemalessStageId(items, lists.schemas, role);
      if (stageId) {
        stageSchemaFor(resource, { role, id: stageId });
        return;
      }
      stageSchema(resource, role);
    },
    [
      lists.sources,
      lists.sinks,
      lists.transforms,
      lists.schemas,
      clearDraftFor,
      stageSchema,
      stageSchemaFor,
    ],
  );
  return useMemo(
    () => ({ stageSchema, stageSchemaFor, stageDraftedSchema, attachSchema }),
    [stageSchema, stageSchemaFor, stageDraftedSchema, attachSchema],
  );
}

// Reopening a remembered instance draft instead of refetching: schema and
// transform rows bring back the in-flight edits — leaving a row never
// resets its draft. False when the row has no draft; the caller loads the
// stored content fresh.
function restoredDraft(
  id: string,
  store: { texts: Record<string, string>; origins: Record<string, string> },
  restore: (text: string, origin: string) => void,
): boolean {
  const draft = store.texts[id];
  if (draft == null) return false;
  restore(draft, store.origins[id] ?? draft);
  return true;
}

function restoredInstanceDraft(options: {
  kind: "sample" | "script" | "schema";
  id: string;
  drafts: SchemaDrafts;
  scripts: ScriptDrafts;
  editor: EditorContents;
}): boolean {
  const { kind, id, drafts, scripts, editor } = options;
  if (kind === "schema") {
    return restoredDraft(
      id,
      { texts: drafts.schemaDrafts, origins: drafts.draftOrigins },
      editor.restoreSchemaEdit,
    );
  }
  if (kind === "script") {
    return restoredDraft(
      id,
      { texts: scripts.scriptDrafts, origins: scripts.scriptDraftOrigins },
      editor.restoreScriptEdit,
    );
  }
  return false;
}

// A SCHEMA editor slot reopens its own draft over the zero state.
function reopenSlotDraft(
  id: string,
  drafts: SchemaDrafts,
  editor: EditorContents,
) {
  editor.clearEditor("schema");
  const draft = drafts.schemaDrafts[id];
  if (draft) editor.setSchemaDocument(draft);
}

// Newly entering a "use the editor" row starts a fresh cycle: that editor
// resets to its zero state — except a SCHEMA slot or the TRANSFORM
// pseudo-row holding an unsaved draft, which reopens the draft. Re-clicks
// keep what was typed.
function resetEditorRow(options: {
  id: string;
  current: string | null;
  drafts: SchemaDrafts;
  scripts: ScriptDrafts;
  editor: EditorContents;
}) {
  const { id, current, drafts, scripts, editor } = options;
  const reset = editorToReset(id, current);
  if (reset === null) return;
  if (reset === "schema") {
    reopenSlotDraft(id, drafts, editor);
    return;
  }
  editor.clearEditor(reset);
  // Entering an untitled script row reopens ITS kept draft; the pseudo-row
  // itself always starts a fresh cycle (its typing mints a new draft).
  if (reset === "script" && isUntitledScriptId(id)) {
    const draft = scripts.scriptDrafts[id];
    if (draft) editor.setScriptDocument(draft);
  }
}

// Selecting a schema or transform row with a remembered instance draft
// brings the in-flight edits back instead of refetching the stored
// content. Other kinds load fresh.
function useStagedContentLoader(
  lists: StagedLists,
  editor: EditorContents,
  drafts: SchemaDrafts,
  scriptDrafts: ScriptDrafts,
) {
  const { loadSample, loadScript, loadSchema } = editor;
  const loaders = useMemo(
    () =>
      ({ sample: loadSample, script: loadScript, schema: loadSchema }) as const,
    [loadSample, loadScript, loadSchema],
  );
  return useCallback(
    (id: string) => {
      const staged = stagedContentFor(id, lists);
      if (!staged) return;
      const restored = restoredInstanceDraft({
        kind: staged.kind,
        id,
        drafts,
        scripts: scriptDrafts,
        editor,
      });
      if (restored) return;
      loaders[staged.kind](staged.resource);
    },
    [lists, loaders, drafts, scriptDrafts, editor],
  );
}

// Nav-bar row selection behaviors on top of the raw selection state.
export function useRowSelection(options: {
  lists: StagedLists;
  editor: EditorContents;
  effectiveSelectedId: string | null;
  drafts: SchemaDrafts;
  scriptDrafts: ScriptDrafts;
  setEditorSource: SetTreePill;
  setSelectedId: SetSelectedId;
}) {
  const {
    lists,
    editor,
    effectiveSelectedId,
    drafts,
    scriptDrafts,
    setEditorSource,
    setSelectedId,
  } = options;
  const loadStagedContent = useStagedContentLoader(
    lists,
    editor,
    drafts,
    scriptDrafts,
  );

  const toggleSelected = useCallback(
    (id: string) => {
      // Something is always selected: a staged stage row's second click
      // hands the selection to its tab's pseudo-row and a schema row's to
      // the stage row heading its block (entered fresh, like a direct
      // click on it — a staged target's content loads into the editor);
      // pseudo-rows don't toggle off. The compare is against the EFFECTIVE
      // selection: the derived default (a fresh page's Source row) toggles
      // on its first click like any row that shows selected — raw-only
      // would swallow that click just making the selection explicit.
      if (effectiveSelectedId === id) {
        const target = deselectTarget(id, lists.roleOf, lists);
        if (target === null) return;
        resetEditorRow({
          id: target,
          current: effectiveSelectedId,
          drafts,
          scripts: scriptDrafts,
          editor,
        });
        loadStagedContent(target);
        setEditorSource(null);
        setSelectedId(target);
        return;
      }
      resetEditorRow({
        id,
        current: effectiveSelectedId,
        drafts,
        scripts: scriptDrafts,
        editor,
      });
      // Clicking a staged row refreshes the editor with that item's
      // content — like /studio's resource-select; a row with a remembered
      // draft reopens the draft instead.
      loadStagedContent(id);
      setEditorSource(null);
      setSelectedId(id);
    },
    [
      effectiveSelectedId,
      lists,
      loadStagedContent,
      drafts,
      scriptDrafts,
      editor,
      setEditorSource,
      setSelectedId,
    ],
  );

  const deselectIf = useCallback(
    (id: string) => {
      setSelectedId((prev) => (prev === id ? null : prev));
    },
    [setSelectedId],
  );

  // Re-entering a stage tab brings back its last selection. Unlike
  // toggleSelected there is no fresh-entry reset — a tab switch must not
  // wipe what was typed in the tab's editor. Only schema content needs
  // refreshing: schemaDocument is shared by both stage tabs, so a staged
  // schema row reopens its remembered draft or reloads its stored
  // definition (a stale document under a restored row could otherwise be
  // saved under the wrong id) and a SCHEMA editor slot reopens its own
  // draft or zero state. Input/script documents are only written by their
  // own tab's flows and stay as-is.
  const restoreSelection = useCallback(
    (id: string) => {
      if (isSchemaEditorId(id)) {
        reopenSlotDraft(id, drafts, editor);
      } else if (schemaIdOfInstance(id) !== null) {
        loadStagedContent(id);
      }
      setEditorSource(null);
      setSelectedId(id);
    },
    [loadStagedContent, drafts, editor, setEditorSource, setSelectedId],
  );

  return useMemo(
    () => ({ toggleSelected, deselectIf, restoreSelection }),
    [toggleSelected, deselectIf, restoreSelection],
  );
}

// Removing a staged row; deleting the selected row (including the
// default-fallback source row) hands the editor its zero state, deleting
// anything else leaves the editor and selection alone.
function useUnstage(
  lists: StagedLists,
  clearEditor: EditorContents["clearEditor"],
  effectiveSelectedId: string | null,
  setSelectedId: SetSelectedId,
) {
  return useCallback(
    (id: string) => {
      const role = unstageHoldsSelection(effectiveSelectedId, id)
        ? lists.roleOf(id)
        : null;
      if (role) {
        const next = selectionAfterUnstage(role, effectiveSelectedId);
        if (next.clearEditor) clearEditor(next.clearEditor);
        setSelectedId(next.selectedId);
      }
      lists.removeEverywhere(id);
    },
    [
      effectiveSelectedId,
      lists.roleOf,
      lists.removeEverywhere,
      clearEditor,
      setSelectedId,
    ],
  );
}

// A selection anywhere inside a renamed transform block follows the new
// id: the row itself, its SCHEMA slot, or a schema instance keyed under
// the slot.
function selectionAfterTransformRename(
  prev: string | null,
  oldId: StagedId,
  newId: StagedId,
): string | null {
  if (prev === null) return null;
  if (prev === oldId) return newId;
  const oldSlot = schemaEditorId(oldId);
  const insideBlock = prev === oldSlot || prev.endsWith(`|${oldSlot}`);
  if (!insideBlock) return prev;
  return `${prev.slice(0, prev.length - oldSlot.length)}${schemaEditorId(newId)}`;
}

// A source/sink dropped while its tab's bottom block holds an *unsaved*
// schema draft joins that block: the draft (selected or not) moves into
// the new stage's SCHEMA slot, and its save will then attach there.
// Skipped when a staged unattached schema exists — the stage adopts that
// one instead (schemasAfterStageStaged) and its slot wouldn't be free for
// the draft. Dropping any stage also selects its new row, like schema
// drops do — a dropped transform additionally loads its script into the
// editor and a dropped source its sample document (what a row click would
// do; an edited input stays put via the loadSample guard). The draft
// carve-out stays: when the moved draft held the selection, it follows
// into the slot's editor row instead so what's on screen stays the draft.
// A staged stage adopts its own tab's pending SCHEMA-slot draft — true
// when the draft was on screen and keeps it (the caller re-points the
// selection instead of selecting the new row).
function draftKeptScreen(
  role: StagedRole,
  resource: StagedResource,
  ctx: {
    schemas: StagedSchema[];
    loadSample: (resource: StagedResource) => void;
    moveDraftToStage: (role: StageTabRole, stageId: string) => boolean;
    selectedId: string | null;
  },
): boolean {
  // Only the drop's own tab's pending schema gets adopted into the new
  // stage's slot — another tab's one leaves the slot free for the draft.
  const slotFree = !ctx.schemas.some((s) => isPendingOn(s, role));
  if (!slotFree) return false;
  if (!ctx.moveDraftToStage(role, resource.id)) return false;
  if (ctx.selectedId !== unattachedSchemaEditorId(role)) return false;
  // The draft keeps the screen; the sample still loads behind it.
  if (role === "source") ctx.loadSample(resource);
  return true;
}

function useStageResource(options: {
  lists: StagedLists;
  editor: EditorContents;
  drafts: SchemaDrafts;
  selectedId: string | null;
  setSelectedId: SetSelectedId;
  setEditorSource: SetTreePill;
  seedSchemaDraft: (resource: StagedResource) => void;
}) {
  const { lists, editor, drafts, selectedId } = options;
  const { setSelectedId, setEditorSource, seedSchemaDraft } = options;
  return useCallback(
    (role: StagedRole, resource: StagedResource) => {
      lists.stage(role, resource);
      // A dropped source or sink also seeds its SCHEMA slot with the
      // introspected majority shape (TTTL-179 gap 4) — old /studio's auto
      // schema; a sink's existing documents shape what a write should
      // look like. Transforms stage scripts — nothing to introspect.
      if (role !== "transform") seedSchemaDraft(resource);
      // The new row is selected only once its script/sample is committed
      // to the editor (the loaders' onLoaded): selecting first would flip
      // the editor to a not-yet-loaded document and flash its placeholder
      // while the fetch is in flight (the drop-transition blink).
      const selectRow = () => {
        setSelectedId(resource.id);
        setEditorSource(null);
      };
      if (role === "transform") {
        editor.loadScript(resource, selectRow);
        return;
      }
      if (
        draftKeptScreen(role, resource, {
          schemas: lists.schemas,
          loadSample: editor.loadSample,
          moveDraftToStage: drafts.moveDraftToStage,
          selectedId,
        })
      ) {
        setSelectedId(schemaEditorId(resource.id));
        return;
      }
      if (role === "source") {
        editor.loadSample(resource, selectRow);
        return;
      }
      selectRow();
    },
    [
      lists.stage,
      lists.schemas,
      drafts.moveDraftToStage,
      editor.loadScript,
      editor.loadSample,
      selectedId,
      setSelectedId,
      setEditorSource,
      seedSchemaDraft,
    ],
  );
}

// Sink/transform staging, unstaging and renames composed with the drafts.
export function useSinkDraftOps(options: {
  lists: StagedLists;
  editor: EditorContents;
  drafts: SchemaDrafts;
  scriptDrafts: ScriptDrafts;
  effectiveSelectedId: string | null;
  selectedId: string | null;
  setSelectedId: SetSelectedId;
  setEditorSource: SetTreePill;
  seedSchemaDraft: (resource: StagedResource) => void;
}) {
  const { lists, editor, drafts, scriptDrafts, effectiveSelectedId } = options;
  const { selectedId, setSelectedId, setEditorSource, seedSchemaDraft } =
    options;

  const baseUnstage = useUnstage(
    lists,
    editor.clearEditor,
    effectiveSelectedId,
    setSelectedId,
  );
  // Unstaging a sink takes its SCHEMA slot's unsaved draft with it; a
  // selection sitting on that slot's rows falls back to the default. A
  // fully removed schema also drops its remembered instance drafts.
  const unstage = useCallback(
    (id: string) => {
      baseUnstage(id);
      drafts.clearDraftsForSchema(id);
      scriptDrafts.clearScriptDraft(id);
      scriptDrafts.forgetSavedScript(id);
      const slotRowId = schemaEditorId(id);
      drafts.clearDraftFor(slotRowId);
      if (selectedId === slotRowId) {
        setSelectedId(null);
        editor.clearEditor("schema");
      }
    },
    [
      baseUnstage,
      drafts.clearDraftFor,
      drafts.clearDraftsForSchema,
      scriptDrafts.clearScriptDraft,
      scriptDrafts.forgetSavedScript,
      selectedId,
      editor.clearEditor,
      setSelectedId,
    ],
  );

  // A script rename in one place: the staged lists re-point (row id + schema
  // attachments), the row's draft and its SCHEMA slot's drafts move to the
  // new id, and a selection anywhere inside the renamed block follows.
  const renameTransform = useCallback(
    (
      oldId: string,
      next: { id: string; name: string; provider?: StagedResource["provider"] },
    ) => {
      lists.renameTransformRow(oldId, next);
      scriptDrafts.moveScriptDraft(oldId, next.id);
      drafts.moveBlockDrafts(schemaEditorId(oldId), schemaEditorId(next.id));
      setSelectedId((prev) =>
        selectionAfterTransformRename(prev, oldId, next.id),
      );
    },
    [
      lists.renameTransformRow,
      scriptDrafts.moveScriptDraft,
      drafts.moveBlockDrafts,
      setSelectedId,
    ],
  );

  const stageResource = useStageResource({
    lists,
    editor,
    drafts,
    selectedId,
    setSelectedId,
    setEditorSource,
    seedSchemaDraft,
  });

  return useMemo(
    () => ({ stageResource, unstage, renameTransform }),
    [stageResource, unstage, renameTransform],
  );
}

// Clears a staged schema's pending (bottom block) state on ONE tab.
const withPendingCleared = (
  prev: StagedSchema[],
  schemaId: StagedId,
  tab: StageTabRole,
): StagedSchema[] =>
  prev.map((s) => (s.id === schemaId ? withoutPendingOn(s, tab) : s));

// Whether detaching the given target leaves the schema referenced nowhere —
// no attachments left, not pending in any bottom block.
function lastReference(schema: StagedSchema, targetId: StagedId): boolean {
  const remaining = schema.attachments.filter((a) => a.id !== targetId);
  return remaining.length === 0 && !isPendingAnywhere(schema);
}

// Whether the schema is still referenced once the given tab lets go of it —
// attached to any stage, or pending in another tab's bottom block.
function referencedBeyondTab(schema: StagedSchema, tab: StageTabRole): boolean {
  if (schema.attachments.length > 0) return true;
  return (schema.pending ?? []).some((t) => t !== tab);
}

// A removed block row releases its remembered draft, and — when it held
// the selection as its per-block instance — the selection too, returning
// the schema editor to zero state.
function useReleaseBlockRow(options: {
  editor: EditorContents;
  drafts: SchemaDrafts;
  selectedId: string | null;
  setSelectedId: SetSelectedId;
}) {
  const { editor, drafts, selectedId, setSelectedId } = options;
  return useCallback(
    (schemaId: string, blockKey: string) => {
      const instanceId = schemaInstanceId(schemaId, blockKey);
      drafts.clearDraftFor(instanceId);
      if (selectedId !== instanceId) return;
      setSelectedId(null);
      editor.clearEditor("schema");
    },
    [drafts.clearDraftFor, selectedId, setSelectedId, editor.clearEditor],
  );
}

// The per-block schema row operations: detach from one stage, clear a
// bottom block's waiting state, rename — plus the selection/draft cleanup
// a removed block row needs.
export function useSchemaBlockOps(options: {
  lists: StagedLists;
  editor: EditorContents;
  drafts: SchemaDrafts;
  unstage: (id: string) => void;
  selectedId: string | null;
  setSelectedId: SetSelectedId;
}) {
  const { lists, editor, drafts, unstage, selectedId, setSelectedId } = options;
  const releaseBlockRow = useReleaseBlockRow({
    editor,
    drafts,
    selectedId,
    setSelectedId,
  });

  // A schema can be attached to any number of stages, across both stage
  // tabs — a group's remove only detaches it from THAT stage; removing the
  // last reference unstages it entirely, mirroring the eviction rule.
  const detachSchema = useCallback(
    (schemaId: string, target: { role: StagedRole; id: StagedId }) => {
      const schema = lists.schemas.find((s) => s.id === schemaId);
      if (!schema) return;
      if (lastReference(schema, target.id)) {
        unstage(schemaId);
        return;
      }
      lists.setSchemas((prev) => toggleAttachment(prev, schemaId, target));
      releaseBlockRow(schemaId, schemaEditorId(target.id));
    },
    [lists.schemas, lists.setSchemas, unstage, releaseBlockRow],
  );

  // The bottom-block row's remove: clears the waiting state; a schema
  // referenced nowhere else leaves staging entirely.
  const removePending = useCallback(
    (schemaId: string, tab: StageTabRole) => {
      const schema = lists.schemas.find((s) => s.id === schemaId);
      if (!schema) return;
      // Referenced nowhere else — no stage, no other tab's bottom block —
      // the schema leaves staging entirely.
      if (!referencedBeyondTab(schema, tab)) {
        unstage(schemaId);
        return;
      }
      lists.setSchemas((prev) => withPendingCleared(prev, schemaId, tab));
      if (isPendingOn(schema, tab)) {
        releaseBlockRow(schemaId, unattachedSchemaEditorId(tab));
      }
    },
    [lists.schemas, lists.setSchemas, unstage, releaseBlockRow],
  );

  // Renames a staged schema in every block that shows it (the backend
  // rename is the caller's job — this updates the staged rows).
  const renameSchema = useCallback(
    (schemaId: string, name: string) => {
      lists.setSchemas((prev) => withSchemaRenamed(prev, schemaId, name));
    },
    [lists.setSchemas],
  );

  return useMemo(
    () => ({ detachSchema, removePending, renameSchema }),
    [detachSchema, removePending, renameSchema],
  );
}

// The fixed pseudo-row ids are always restorable.
const EDITOR_ROW_IDS = new Set<string>([
  SOURCE_EDITOR_ID,
  TRANSFORM_EDITOR_ID,
  SINK_EDITOR_ID,
  SCHEMA_EDITOR_ID,
]);

// A remembered id may have gone stale while another tab unstaged / cleared
// things — only rows that still exist come back.
function validForRestore(
  id: string,
  roleOf: (id: string) => StagedKind | null,
): boolean {
  if (EDITOR_ROW_IDS.has(id)) return true;
  if (id === unattachedSchemaEditorId("source")) return true;
  if (id === unattachedSchemaEditorId("transform")) return true;
  if (isSchemaEditorId(id)) {
    // Per-item SCHEMA slot: its stage item must still be staged.
    return roleOf(id.slice(SCHEMA_EDITOR_ID.length + 1)) !== null;
  }
  // Schema row instance: its schema must still be staged.
  const instanceSchemaId = schemaIdOfInstance(id);
  if (instanceSchemaId !== null) return roleOf(instanceSchemaId) === "schema";
  return roleOf(id) !== null;
}

// A stale pill whose content isn't on the entered tab's editor would mark
// a row whose content isn't on screen: the sink tab only shows schema
// content, the transform tab scripts + schemas.
function pillForTab(tab: StageTabId, pill: TreePill): TreePill {
  if (pill === null) return null;
  if (tab === "sink") return pill.kind === "schema" ? pill : null;
  if (tab === "transform") return pill.kind === "input" ? null : pill;
  return pill;
}

// The entered tab restores where the user was — unless a live preview pill
// holds the attention (it's the fresher intent) or the remembered row no
// longer exists; then the tab defaults apply.
function restorableSelection(
  remembered: string | null,
  pill: TreePill,
  roleOf: (id: string) => StagedKind | null,
): string | null {
  if (remembered === null) return null;
  if (pill !== null) return null;
  return validForRestore(remembered, roleOf) ? remembered : null;
}

// Every tab remembers its last selection (raw, not the derived default)
// so switching away and back lands the user where they were. A tab with
// nothing to restore selects its default row instead (selectTabDefault) —
// the first staged row for viewing, never a blank landing.
export function useTabMemory(options: {
  stageTab: StageTabId;
  setStageTab: (tab: StageTabId) => void;
  selectedId: string | null;
  editorSource: TreePill;
  setEditorSource: SetTreePill;
  setSelectedId: SetSelectedId;
  roleOf: (id: string) => StagedKind | null;
  restoreSelection: (id: string) => void;
  selectTabDefault: (tab: StageTabId) => void;
}) {
  const { stageTab, setStageTab, selectedId, editorSource } = options;
  const { setEditorSource, setSelectedId, roleOf, restoreSelection } = options;
  const { selectTabDefault } = options;
  const tabSelections = useRef<Partial<Record<StageTabId, string | null>>>({});

  return useCallback(
    (tab: StageTabId) => {
      const prevTab = stageTab;
      if (tab !== prevTab) tabSelections.current[prevTab] = selectedId;
      setStageTab(tab);
      const pill = pillForTab(tab, editorSource);
      setEditorSource(pill);
      if (tab === prevTab) return;
      const remembered = restorableSelection(
        tabSelections.current[tab] ?? null,
        pill,
        roleOf,
      );
      if (remembered !== null) {
        restoreSelection(remembered);
        return;
      }
      setSelectedId(null);
      // A live preview pill keeps the attention; otherwise the entered
      // tab's default row takes the selection like a click.
      if (pill === null) selectTabDefault(tab);
    },
    [
      stageTab,
      setStageTab,
      selectedId,
      editorSource,
      setEditorSource,
      setSelectedId,
      roleOf,
      restoreSelection,
      selectTabDefault,
    ],
  );
}
